import { FetchResult } from '@apollo/client';
import { CreateOwnerContractTemplate } from '../templates';
import {
  useCreateOwnerContractMutation,
  CreateOwnerContractMutation,
} from '@/graphql/generated';
import {
  useErrorHandlerContext,
  useOpeningElementsControllerContext,
  useUserLanguage,
  useCreateOwnerContractContext,
} from '@/customHooks';
import textsData from '@/textData/CreateContractsTexts.json';
import { useNavigate } from 'react-router';
import { useState, useMemo } from 'react';
import { Stepper } from '../molecules';
import { Button } from '../atoms';

export const CreateOwnerContractPage = () => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;
  const ownerTexts = texts.ownerContract;

  const [step, setStep] = useState(0);

  const steps = useMemo(
    () => ownerTexts.steps,
    [ownerTexts]
  );
  
  const maxStep = steps.length - 1;

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext();
  const navigate = useNavigate();
  const { state, buttonState } = useCreateOwnerContractContext();
  const { open: errorHandle } = useErrorHandlerContext();

  const [createOwnerContract, { loading: isSubmitting }] = useCreateOwnerContractMutation();

  const onSuccessHandle = (response: FetchResult<CreateOwnerContractMutation>) => {
    confirmDialogModalOpen({
      title: texts.ownerContractModal.title,
      description: `${texts.ownerContractModal.description} ${response.data?.createOwnerContract.id}`,
    });
    navigate(-1);
  };

  const onFinishHandle = () => {
    const { ownerName, propertyName, ...input } = state;
    
    createOwnerContract({
      variables: {
        input,
      },
    })
      .then(onSuccessHandle)
      .catch(errorHandle);
  };

  const handleNext = () => {
    if (step < maxStep) {
      setStep((prev) => prev + 1);
    } else {
      onFinishHandle();
    }
  };

  const handlePrevious = () => {
    if (step > 0) {
      setStep((prev) => prev - 1);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="flex flex-col gap-8 py-8 px-4 max-w-7xl mx-auto min-h-[calc(100vh-80px)]">
      <div className="flex flex-col gap-2 text-center">
        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
          {texts.templateTextOwner[0].title}
        </h1>
        <p className="text-gray-500 max-w-2xl mx-auto">
          {texts.templateTextOwner[0].description}
        </p>
      </div>

      <div className="flex justify-center w-full overflow-x-auto pb-4">
        <Stepper
          steps={steps}
          currentStep={step}
        />
      </div>

      <div className="flex-1 bg-(--color-background-1) rounded-3xl p-6 md:p-10 shadow-sm border border-gray-100">
        <CreateOwnerContractTemplate step={step} />
      </div>

      <div className="flex justify-center gap-4 pt-4">
        <Button
          secondary
          onClick={handlePrevious}
          className="w-40"
        >
          {step === 0 ? texts.cancelButton : userLanguage === 'es' ? 'Anterior' : 'Previous'}
        </Button>
        <Button
          onClick={handleNext}
          isLoading={isSubmitting}
          disabled={buttonState || isSubmitting}
          className="w-40"
        >
          {step < maxStep
            ? userLanguage === 'es' ? 'Siguiente' : 'Next'
            : userLanguage === 'es' ? 'Confirmar' : 'Confirm'}
        </Button>
      </div>
    </div>
  );
};

